import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, BreadcrumbGroup, Button, Container, Grid, Input, SpaceBetween, StatusIndicator } from "@amzn/awsui-components-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { convertToLocalTime } from "./Utility";
import { useSelector } from "react-redux";
import './styles.scss';
import { downloadTieOutResults } from "./tieOutPdfUtils";

const mockGetHistory = () => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve([
                { section: "Cover Page", version: "Pre-WB", status: "Tie-Out Marked Complete", executed_by: "jsmith", execution_date: "2026-01-01T16:00:00Z", request_id: "req-001" },
                { section: "Cover Page", version: "Pre-WB", status: "Tie-Out Created", executed_by: "jsmith", execution_date: "2025-12-31T16:00:00Z", request_id: "req-002" },
                { section: "I7.29 - Effects of FX", version: "Pre-WB", status: "Tie-Out Marked Complete", executed_by: "adoe", execution_date: "2026-01-01T16:00:00Z", request_id: "req-003" },
                { section: "Balance Sheet", version: "Pre-WB", status: "Tie-Out Marked Complete", executed_by: "jsmith", execution_date: "2026-01-01T14:00:00Z", request_id: "req-004" },
                { section: "Balance Sheet", version: "Pre-WB", status: "Tie-Out Created", executed_by: "jsmith", execution_date: "2025-12-30T10:00:00Z", request_id: "req-005" },
                { section: "Cash Flow Statement", version: "Pre-WB", status: "Tie-Out Created", executed_by: "adoe", execution_date: "2026-01-01T16:00:00Z", request_id: "req-006" },
                { section: "Consolidated Statement of Operations", version: "Pre-WB", status: "Tie-Out Marked Complete", executed_by: "bwilson", execution_date: "2026-01-01T12:00:00Z", request_id: "req-007" },
                { section: "Note 3 — Revenue Recognition", version: "Pre-WB", status: "Tie-Out Created", executed_by: "bwilson", execution_date: "2025-12-31T08:00:00Z", request_id: "req-008" },
                { section: "Note 7 — Commitments and Contingencies", version: "Pre-WB", status: "Tie-Out Created", executed_by: "jsmith", execution_date: "2026-01-01T16:00:00Z", request_id: "req-009" },
                { section: "Guidance", version: "Pre-WB", status: "Not Run", executed_by: "adoe", execution_date: "2025-12-29T09:00:00Z", request_id: "req-010" },
            ]);
        }, 1000);
    });
};

const TieOutHistoryComponent = (props) => {
    const gridRef = useRef();
    const [historyList, setHistoryList] = useState([]);
    const [quickFilterText, setQuickFilterText] = useState('');
    const [viewResultsDetails, setViewResultsDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadLoader, setDownloadLoader] = useState({});
    const userId = useSelector((globalState) => globalState.auth.userId);

    const defaultColDef = {
        filter: true,
        filterParams: {
            buttons: ['reset'],
            debounceMs: 200
        }
    };

    const downloadResults = async (data) => {
        await downloadTieOutResults(
            data,
            () => setDownloadLoader(prev => ({ ...prev, [data.request_id]: true })),
            () => setDownloadLoader(prev => ({ ...prev, [data.request_id]: false }))
        );
    };

    const columnDefs = useMemo(() => [
        { field: 'section', headerName: 'Section', flex: 2, sortable: true, resizable: true },
        { field: 'version', headerName: 'Version', width: 120, sortable: true, resizable: true },
        { field: 'status', headerName: 'Status', width: 180, sortable: true, resizable: true },
        { field: 'executed_by', headerName: 'Executed By', width: 150, sortable: true, resizable: true },
        {
            field: 'results',
            headerName: 'Results',
            width: 200,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                const isLoadingDownload = downloadLoader?.[params?.data?.request_id];
                return (
                    <SpaceBetween direction="horizontal" size="s">
                        <Button onClick={() => fetchDetails(params?.data)} variant="inline-link">View</Button>
                        <Button
                            iconName="download"
                            variant="icon"
                            onClick={() => downloadResults(params?.data)}
                            disabled={isLoadingDownload}
                        />
                    </SpaceBetween>
                );
            }
        },
        {
            field: 'execution_date',
            headerName: 'Execution Date',
            width: 220,
            sortable: true,
            resizable: true,
            cellRenderer: (params) => { return params.value || ''; },
            valueGetter: (params) => {
                return params?.data?.execution_date ? convertToLocalTime(params.data.execution_date, true) : '';
            }
        },
        { field: 'request_id', headerName: 'ID', width: 150, resizable: true, sortable: true },
    ], [downloadLoader]);

    useEffect(() => {
        fetchHistory();
    }, []);

    useEffect(() => {
        onFilterTextBoxChanged();
    }, [quickFilterText]);

    function onFilterTextBoxChanged() {
        gridRef?.current?.api?.setGridOption(
            "quickFilterText",
            quickFilterText,
        );
    }

    const fetchDetails = (data) => {
        setViewResultsDetails(data);
    };

    const fetchHistory = async () => {
        setIsLoading(true);
        const data = await mockGetHistory();
        const sortedItems = data.sort(
            (a, b) => new Date(b.execution_date) - new Date(a.execution_date)
        );
        setHistoryList(sortedItems);
        setIsLoading(false);
    };

    return (
        <SpaceBetween direction="vertical" size="m">
            <BreadcrumbGroup
                items={[
                    { text: "Tie-Out Dashboard", href: "#" },
                    { text: "History", href: "#" },
                ]}
                ariaLabel="Breadcrumbs"
                onFollow={(event) => {
                    event.preventDefault();
                    if (event.detail.text === "Tie-Out Dashboard" && props.onBack) {
                        props.onBack();
                    }
                }}
            />

            <Container>
                <SpaceBetween direction="vertical" size="m">
                    <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                        <Input
                            onChange={({ detail }) => setQuickFilterText(detail.value)}
                            value={quickFilterText}
                            expandToViewport={true}
                            placeholder='Type to search'
                        />
                    </Grid>

                    <div style={{ width: '100%', height: '50vh' }}>
                        <div
                            className="ag-theme-alpine"
                            style={{ width: '100%', height: '100%', minHeight: '200px' }}
                        >
                            <AgGridReact
                                ref={gridRef}
                                rowData={historyList}
                                defaultColDef={defaultColDef}
                                columnDefs={columnDefs}
                                rowSelection="single"
                                animateRows={true}
                                pagination={true}
                                paginationPageSize={10}
                                paginationPageSizeSelector={[10, 50, 100]}
                                loading={isLoading}
                            />
                        </div>
                    </div>
                </SpaceBetween>
            </Container>

            {viewResultsDetails && (
                <Container>
                    <SpaceBetween direction="vertical" size="m">
                        <Box variant="h3">Tie-Out Result: {viewResultsDetails.section}</Box>
                        <Box>
                            <SpaceBetween direction="horizontal" size="l">
                                <Box><strong>Version:</strong> {viewResultsDetails.version}</Box>
                                <Box><strong>Status:</strong> {viewResultsDetails.status}</Box>
                                <Box><strong>Executed By:</strong> {viewResultsDetails.executed_by}</Box>
                            </SpaceBetween>
                        </Box>
                        <StatusIndicator type="info">
                            Detailed tie-out results for {viewResultsDetails.section} will appear here.
                        </StatusIndicator>
                    </SpaceBetween>
                </Container>
            )}
        </SpaceBetween>
    );
};

export default TieOutHistoryComponent;
